import merge from 'lodash/merge';
import { createTheme, darken, lighten, responsiveFontSizes } from '@mui/material/styles';
import { lightShadows } from './shadows';

const primaryBlue = '#2b1f73';
const primaryGreen = '#00ad03';

const smallScrollBar = {
  overflowY: "overlay",
  "&::-webkit-scrollbar": {
    width: 4,
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: 8,
    borderWidth: 0,
  },
  "&::-webkit-scrollbar-button": {
    height: 4
  },
};

const baseOptions = {
  direction: 'ltr',
  components: {
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: '#eaebeb',
        },
        fallback: {
          height: '75%',
          width: '75%',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        sizeSmall: {
          padding: '4px 10px'
        },
        sizeMedium: {
          padding: '6px 16px'
        },
        sizeLarge: {
          padding: '8px 22px'
        },
        textSizeSmall: {
          padding: '4px 5px'
        },
        textSizeMedium: {
          padding: '6px 8px'
        },
        textSizeLarge: {
          padding: '8px 11x'
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          margin: 0,
          padding: 0,
          outline: 0,
          boxSizing: 'border-box',
        },
        ...smallScrollBar,
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
        },
        '#root': {
          height: '100%',
        },
        '#nprogress': {
          '& .bar': {
            zIndex: '2000 !important',
            background: '#00ad03',
            height: 3,
          },
          '& .peg': {
            boxShadow: '0 0 10px #00ad03, 0 0 5px #00ad03',
          },
          '& .spinner': {
            display: 'none',
          },
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
        },
        'root': {
          '& button': {
            textTransform: 'none'
          }
        }
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: '#14142B',
            fontWeight: 600
          },
        },
      },
    },
  },
  typography: {
    button: {
      fontWeight: 500,
    },
    fontFamily: "'Poppins', sans-serif",
    h1: {
      fontWeight: 600,
      fontSize: '3.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '3rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2.25rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    overline: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 500,
    },
    subtitle2: {
      fontWeight: 500,
    }
  },
};

const themeDefault = {
  direction: 'ltr',
  components: {
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 0.86,
            color: '#42526e',
          },
        },
      },
    },
    MuiSkeleton: {
      defaultProps: {
        animation: 'wave',
      },
    },
    MuiOutlinedInput: {
      variants: [
        {
          props: { customstyle: 'smooth' },
          style: {
            backgroundColor: '#EFF0F6',
            '&.Mui-focused': {
              backgroundColor: '#EFF0F6',
            },
            '&:hover': {
              backgroundColor: darken('#EFF0F6', 0.03),
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#b1b2ba',
              },
            },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#EFF0F6' },
          },
        },
      ],
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'outlined', customstyle: 'dashed' },
          style: {
            borderStyle: `dashed`,
            borderWidth: 2,
            '&:hover': {
              borderStyle: `dashed`,
              borderWidth: 2,
            },
          },
        },
      ],
    },
  },
  shape: {
    borderRadius: 10,
  },
  palette: {
    mode: 'light',
    background: {
      default: '#F4F5F9',
      paper: '#ffffff',
      gradient: `linear-gradient(135deg, ${lighten(primaryBlue, .1)}, ${primaryBlue}, ${darken(primaryBlue, .2)})`
    },
    action: {
      active: '#6b778c',
    },
    primary: {
      contrastText: '#ffffff',
      main: primaryBlue,
      light: '#C4D9FF',
    },
    secondary: {
      contrastText: '#ffffff',
      main: primaryGreen,
      light: '#CFF9E7',
    },
    error: {
      contrastText: '#ffffff',
      main: '#f44336',
      light: '#f9dbd9',
    },
    success: {
      contrastText: '#ffffff',
      main: '#01c851',
    },
    warning: {
      contrastText: '#ffffff',
      main: '#ff9800',
    },
    orange: {
      main: '#FF9C0E',
      light: '#fff0db',
      contrastText: '#ffffff',
    },
    purple: {
      main: '#7400B8',
      contrastText: '#ffffff',
    },
    yellow: {
      main: '#FFDB01',
      contrastText: '#ffffff',
      light: 'FFF7C9',
    },
    grey: {
      contrastText: '#ffffff',
      main: '#A2A4B9',
      light: '#C5CAD2;',
    },
    black: {
      contrastText: '#ffffff',
      main: '#172b4d',
      light: '#C5CAD2;',
    },
    filterColor: {
      main: '#EFF0F6',
      contrastText: '#253858',
    },
    text: {
      primary: '#172b4d',
      secondary: '#6b778c',
    },

  },

  shadows: lightShadows,
};

const createCustomTheme = (config = {}) => {
  let theme = createTheme(merge({}, baseOptions, themeDefault, config));

  return responsiveFontSizes(theme);
};

export const Theme = createCustomTheme();
